// src/services/messaging.service.js
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  onSnapshot,
  serverTimestamp,
  startAfter,
  increment,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "@/firebase";

// Helper to check if we're in a test environment with mocks available
const isTestMode = () => {
  return typeof window !== "undefined" && window.mockMessagingService;
};

// Active listeners for cleanup
const activeListeners = new Map();

// Typing status debounce timers
const typingTimers = new Map();

/**
 * Generate a deterministic conversation ID from teacher and student IDs
 */
const generateConversationId = (teacherId, studentId) => {
  return `${teacherId}_${studentId}`;
};

/**
 * Get or create a conversation between a teacher and student
 * @param {string} teacherId - The teacher's ID
 * @param {string} studentId - The student's ID
 * @param {Object} teacherData - Teacher metadata (name, photo)
 * @param {Object} studentData - Student metadata (name, photo)
 * @returns {Promise<string>} - The conversation ID
 */
export const getOrCreateConversation = async (
  teacherId,
  studentId,
  teacherData = {},
  studentData = {}
) => {
  // Use mock in test mode
  if (isTestMode()) {
    const conversation =
      await window.mockMessagingService.getOrCreateConversation(
        teacherId,
        studentId
      );
    return conversation.id;
  }

  try {
    const conversationId = generateConversationId(teacherId, studentId);
    const conversationRef = doc(db, "conversations", conversationId);
    const conversationDoc = await getDoc(conversationRef);

    if (!conversationDoc.exists()) {
      // Create new conversation
      await setDoc(conversationRef, {
        participants: [teacherId, studentId],
        createdAt: serverTimestamp(),
        lastActivity: serverTimestamp(),
        unreadCount: {
          [teacherId]: 0,
          [studentId]: 0,
        },
        metadata: {
          teacherName: teacherData.name || "Teacher",
          studentName: studentData.name || "Student",
          teacherPhoto: teacherData.photoURL || null,
          studentPhoto: studentData.photoURL || null,
        },
      });
    }

    return conversationId;
  } catch (error) {
    console.error("Error creating/getting conversation:", error);
    throw new Error("Failed to create conversation: " + error.message);
  }
};

/**
 * Get all conversations for a user with real-time updates
 * @param {string} userId - The user's ID
 * @param {Function} callback - Callback function for real-time updates
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToConversations = (userId, callback) => {
  try {
    const conversationsRef = collection(db, "conversations");
    const q = query(
      conversationsRef,
      where("participants", "array-contains", userId),
      orderBy("lastActivity", "desc"),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const conversations = [];
        snapshot.forEach((doc) => {
          conversations.push({
            id: doc.id,
            ...doc.data(),
            lastActivity: doc.data().lastActivity?.toDate?.() || null,
          });
        });
        callback(conversations, null);
      },
      (error) => {
        console.error("Error in conversations listener:", error);
        callback([], error);
      }
    );

    // Store listener for cleanup
    activeListeners.set(`conversations_${userId}`, unsubscribe);
    return unsubscribe;
  } catch (error) {
    console.error("Error setting up conversations listener:", error);
    throw error;
  }
};

/**
 * Subscribe to messages in a conversation with real-time updates
 * @param {string} conversationId - The conversation ID
 * @param {Function} callback - Callback function for real-time updates
 * @param {number} limitCount - Number of messages to fetch (default 50)
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToMessages = (
  conversationId,
  callback,
  limitCount = 50
) => {
  // Use mock in test mode
  if (isTestMode()) {
    return window.mockMessagingService.subscribeToMessages(
      conversationId,
      callback
    );
  }

  try {
    const messagesRef = collection(
      db,
      "conversations",
      conversationId,
      "messages"
    );
    const q = query(
      messagesRef,
      orderBy("timestamp", "desc"),
      limit(limitCount)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const messages = [];
        snapshot.forEach((doc) => {
          messages.unshift({
            id: doc.id,
            ...doc.data(),
            timestamp: doc.data().timestamp?.toDate?.() || null,
          });
        });
        callback(messages, null);
      },
      (error) => {
        console.error("Error in messages listener:", error);
        callback([], error);
      }
    );

    // Store listener for cleanup
    activeListeners.set(`messages_${conversationId}`, unsubscribe);
    return unsubscribe;
  } catch (error) {
    console.error("Error setting up messages listener:", error);
    throw error;
  }
};

/**
 * Send a text message
 * @param {string} conversationId - The conversation ID
 * @param {string} senderId - The sender's ID
 * @param {string} text - The message text
 * @returns {Promise<string>} - The message ID
 */
export const sendTextMessage = async (conversationId, senderId, text) => {
  // Use mock in test mode
  if (isTestMode()) {
    return window.mockMessagingService.sendTextMessage(
      conversationId,
      senderId,
      text
    );
  }

  try {
    if (!text || text.trim().length === 0) {
      throw new Error("Message text cannot be empty");
    }

    // Add message to subcollection
    const messagesRef = collection(
      db,
      "conversations",
      conversationId,
      "messages"
    );
    const messageRef = await addDoc(messagesRef, {
      senderId,
      text: text.trim(),
      type: "text",
      timestamp: serverTimestamp(),
      status: "sent",
    });

    // Update conversation with last message info
    const conversationRef = doc(db, "conversations", conversationId);
    const conversationDoc = await getDoc(conversationRef);

    if (conversationDoc.exists()) {
      const conversationData = conversationDoc.data();
      const otherUserId = conversationData.participants.find(
        (id) => id !== senderId
      );

      await updateDoc(conversationRef, {
        lastMessage: {
          text: text.trim(),
          timestamp: serverTimestamp(),
          senderId,
          type: "text",
        },
        lastActivity: serverTimestamp(),
        [`unreadCount.${otherUserId}`]: increment(1),
      });
    }

    return messageRef.id;
  } catch (error) {
    console.error("Error sending text message:", error);
    throw new Error("Failed to send message: " + error.message);
  }
};

/**
 * Send an image message with attachment
 * @param {string} conversationId - The conversation ID
 * @param {string} senderId - The sender's ID
 * @param {File} imageFile - The image file to upload
 * @param {string} text - Optional caption text
 * @param {Function} progressCallback - Upload progress callback
 * @returns {Promise<string>} - The message ID
 */
export const sendImageMessage = async (
  conversationId,
  senderId,
  imageFile,
  text = "",
  progressCallback = null
) => {
  try {
    // Validate file type
    if (!imageFile.type.startsWith("image/")) {
      throw new Error("File must be an image");
    }

    // Validate file size (max 10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    if (imageFile.size > MAX_FILE_SIZE) {
      throw new Error("Image file must be smaller than 10MB");
    }

    // Upload image to Firebase Storage
    const fileName = `${Date.now()}_${imageFile.name}`;
    const storageRef = ref(storage, `messages/${conversationId}/${fileName}`);

    const uploadTask = uploadBytes(storageRef, imageFile);

    // Handle upload progress if callback provided
    if (progressCallback) {
      uploadTask.then(progressCallback);
    }

    const snapshot = await uploadTask;
    const downloadURL = await getDownloadURL(snapshot.ref);

    // Create message with attachment
    const messagesRef = collection(
      db,
      "conversations",
      conversationId,
      "messages"
    );
    const messageRef = await addDoc(messagesRef, {
      senderId,
      text: text.trim(),
      type: "image",
      timestamp: serverTimestamp(),
      status: "sent",
      attachments: [
        {
          url: downloadURL,
          type: "image",
          name: imageFile.name,
          size: imageFile.size,
          storagePath: `messages/${conversationId}/${fileName}`,
        },
      ],
    });

    // Update conversation
    const conversationRef = doc(db, "conversations", conversationId);
    const conversationDoc = await getDoc(conversationRef);

    if (conversationDoc.exists()) {
      const conversationData = conversationDoc.data();
      const otherUserId = conversationData.participants.find(
        (id) => id !== senderId
      );

      await updateDoc(conversationRef, {
        lastMessage: {
          text: text.trim() || "📷 Image",
          timestamp: serverTimestamp(),
          senderId,
          type: "image",
        },
        lastActivity: serverTimestamp(),
        [`unreadCount.${otherUserId}`]: increment(1),
      });
    }

    return messageRef.id;
  } catch (error) {
    console.error("Error sending image message:", error);
    throw new Error("Failed to send image: " + error.message);
  }
};

/**
 * Send a lesson scheduling request
 * @param {string} conversationId - The conversation ID
 * @param {string} senderId - The sender's ID
 * @param {Date} proposedDate - The proposed lesson date
 * @param {number} duration - The lesson duration in minutes
 * @param {string} text - Optional message text
 * @returns {Promise<string>} - The message ID
 */
export const sendSchedulingMessage = async (
  conversationId,
  senderId,
  proposedDate,
  duration,
  text = ""
) => {
  try {
    const messagesRef = collection(
      db,
      "conversations",
      conversationId,
      "messages"
    );
    const messageRef = await addDoc(messagesRef, {
      senderId,
      text: text.trim() || `Lesson proposal for ${duration} minutes`,
      type: "scheduling",
      timestamp: serverTimestamp(),
      status: "sent",
      schedulingData: {
        proposedDate,
        duration,
        status: "pending",
      },
    });

    // Update conversation
    const conversationRef = doc(db, "conversations", conversationId);
    const conversationDoc = await getDoc(conversationRef);

    if (conversationDoc.exists()) {
      const conversationData = conversationDoc.data();
      const otherUserId = conversationData.participants.find(
        (id) => id !== senderId
      );

      await updateDoc(conversationRef, {
        lastMessage: {
          text: `📅 ${text.trim() || "Lesson proposal"}`,
          timestamp: serverTimestamp(),
          senderId,
          type: "scheduling",
        },
        lastActivity: serverTimestamp(),
        [`unreadCount.${otherUserId}`]: increment(1),
      });
    }

    return messageRef.id;
  } catch (error) {
    console.error("Error sending scheduling message:", error);
    throw new Error("Failed to send lesson proposal: " + error.message);
  }
};

/**
 * Mark messages as read
 * @param {string} conversationId - The conversation ID
 * @param {string} userId - The user's ID
 * @param {string[]} messageIds - Array of message IDs to mark as read (optional, marks all if not provided)
 * @returns {Promise<boolean>} - Success status
 */
export const markMessagesAsRead = async (
  conversationId,
  userId,
  messageIds = []
) => {
  // Use mock in test mode
  if (isTestMode()) {
    return window.mockMessagingService.markMessagesAsRead(
      conversationId,
      userId
    );
  }

  try {
    if (messageIds.length > 0) {
      // Mark specific messages as read
      const updatePromises = messageIds.map((messageId) => {
        const messageRef = doc(
          db,
          "conversations",
          conversationId,
          "messages",
          messageId
        );
        return updateDoc(messageRef, { status: "read" });
      });
      await Promise.all(updatePromises);
    }

    // Reset unread count for this user
    const conversationRef = doc(db, "conversations", conversationId);
    await updateDoc(conversationRef, {
      [`unreadCount.${userId}`]: 0,
    });

    return true;
  } catch (error) {
    console.error("Error marking messages as read:", error);
    throw new Error("Failed to mark messages as read: " + error.message);
  }
};

/**
 * Search messages within a conversation
 * @param {string} conversationId - The conversation ID
 * @param {string} searchQuery - The search query
 * @param {number} limitCount - Number of results to return
 * @returns {Promise<Array>} - Array of matching messages
 */
export const searchMessages = async (
  conversationId,
  searchQuery,
  limitCount = 20
) => {
  try {
    if (!searchQuery || searchQuery.trim().length === 0) {
      return [];
    }

    const messagesRef = collection(
      db,
      "conversations",
      conversationId,
      "messages"
    );
    const q = query(
      messagesRef,
      orderBy("timestamp", "desc"),
      limit(100) // Search within last 100 messages
    );

    const snapshot = await getDocs(q);
    const results = [];
    const searchTerm = searchQuery.toLowerCase();

    snapshot.forEach((doc) => {
      const messageData = { id: doc.id, ...doc.data() };

      // Simple text search - in production, consider using full-text search
      if (
        messageData.text &&
        messageData.text.toLowerCase().includes(searchTerm)
      ) {
        results.push({
          ...messageData,
          timestamp: messageData.timestamp?.toDate?.() || null,
        });
      }
    });

    return results.slice(0, limitCount);
  } catch (error) {
    console.error("Error searching messages:", error);
    throw new Error("Failed to search messages: " + error.message);
  }
};

/**
 * Load more messages (pagination)
 * @param {string} conversationId - The conversation ID
 * @param {Object} lastMessage - The last message object for pagination
 * @param {number} limitCount - Number of messages to load
 * @returns {Promise<Array>} - Array of older messages
 */
export const loadMoreMessages = async (
  conversationId,
  lastMessage,
  limitCount = 20
) => {
  try {
    const messagesRef = collection(
      db,
      "conversations",
      conversationId,
      "messages"
    );

    let q;
    if (lastMessage && lastMessage.timestamp) {
      q = query(
        messagesRef,
        orderBy("timestamp", "desc"),
        startAfter(lastMessage.timestamp),
        limit(limitCount)
      );
    } else {
      q = query(messagesRef, orderBy("timestamp", "desc"), limit(limitCount));
    }

    const snapshot = await getDocs(q);
    const messages = [];

    snapshot.forEach((doc) => {
      messages.unshift({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate?.() || null,
      });
    });

    return messages;
  } catch (error) {
    console.error("Error loading more messages:", error);
    throw new Error("Failed to load more messages: " + error.message);
  }
};

/**
 * Update typing status
 * @param {string} conversationId - The conversation ID
 * @param {string} userId - The user's ID
 * @param {boolean} isTyping - Whether the user is typing
 * @returns {Promise<boolean>} - Success status
 */
export const updateTypingStatus = async (conversationId, userId, isTyping) => {
  // Use mock in test mode
  if (isTestMode()) {
    return window.mockMessagingService.updateTypingStatus(
      conversationId,
      userId,
      isTyping
    );
  }

  try {
    // Clear existing timer
    const timerKey = `${conversationId}_${userId}`;
    if (typingTimers.has(timerKey)) {
      clearTimeout(typingTimers.get(timerKey));
      typingTimers.delete(timerKey);
    }

    // This would typically update a separate typing_status collection
    // For now, we'll use a simple debounced approach
    if (isTyping) {
      // Set auto-clear timer
      const timer = setTimeout(() => {
        updateTypingStatus(conversationId, userId, false);
      }, 3000); // Auto-clear after 3 seconds

      typingTimers.set(timerKey, timer);
    }

    return true;
  } catch (error) {
    console.error("Error updating typing status:", error);
    return false;
  }
};

/**
 * Subscribe to typing status for a conversation
 * @param {string} conversationId - The conversation ID
 * @param {Function} callback - Callback function for typing status updates
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToTypingStatus = (conversationId, callback) => {
  // Use mock in test mode
  if (isTestMode()) {
    return window.mockMessagingService.subscribeToTypingStatus(
      conversationId,
      callback
    );
  }

  // In a real implementation, this would listen to a typing_status collection
  // For now, return a no-op function since typing is handled through local state

  // Call callback immediately with empty typing status
  setTimeout(() => callback({}), 50);

  // Return unsubscribe function (no-op for now)
  return () => {};
};

/**
 * Delete an attachment from storage
 * @param {string} storagePath - The Firebase Storage path
 * @returns {Promise<boolean>} - Success status
 */
export const deleteAttachment = async (storagePath) => {
  try {
    const fileRef = ref(storage, storagePath);
    await deleteObject(fileRef);
    return true;
  } catch (error) {
    console.error("Error deleting attachment:", error);
    return false;
  }
};

/**
 * Get conversation details
 * @param {string} conversationId - The conversation ID
 * @returns {Promise<Object>} - Conversation data
 */
export const getConversation = async (conversationId) => {
  try {
    const conversationRef = doc(db, "conversations", conversationId);
    const conversationDoc = await getDoc(conversationRef);

    if (!conversationDoc.exists()) {
      throw new Error("Conversation not found");
    }

    return {
      id: conversationDoc.id,
      ...conversationDoc.data(),
      lastActivity: conversationDoc.data().lastActivity?.toDate?.() || null,
    };
  } catch (error) {
    console.error("Error getting conversation:", error);
    throw error;
  }
};

/**
 * Cleanup all active listeners
 */
export const cleanupListeners = () => {
  activeListeners.forEach((unsubscribe, key) => {
    console.log("Cleaning up listener:", key);
    unsubscribe();
  });
  activeListeners.clear();

  // Clear typing timers
  typingTimers.forEach((timer) => {
    clearTimeout(timer);
  });
  typingTimers.clear();
};

/**
 * Get unread message count for a user across all conversations
 * @param {string} userId - The user's ID
 * @returns {Promise<number>} - Total unread count
 */
export const getTotalUnreadCount = async (userId) => {
  try {
    const conversationsRef = collection(db, "conversations");
    const q = query(
      conversationsRef,
      where("participants", "array-contains", userId)
    );

    const snapshot = await getDocs(q);
    let totalUnread = 0;

    snapshot.forEach((doc) => {
      const data = doc.data();
      totalUnread += data.unreadCount?.[userId] || 0;
    });

    return totalUnread;
  } catch (error) {
    console.error("Error getting total unread count:", error);
    return 0;
  }
};
